import React from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useQuery } from '@tanstack/react-query';

import moment from 'moment';

import { QUERY_STALE_TIME_DEFAULT } from 'constants/globals';
import { lastLoadLayoutDateService } from 'service/last-dates';
import type { AppDispatch, RootState } from 'store/configureStore';
import { createReportSelectors } from 'store/reportStore';

import { getFirstDayOfTheMonth } from 'utils/date';

const useReportStore = (
  sliceName: keyof RootState['entities'],
  withDates = false,
) => {
  const dispatch = useDispatch<AppDispatch>();
  const selectors = createReportSelectors(sliceName);

  // Obtener dates del store antes del useEffect
  const dates = useSelector(selectors.getDates);

  const { data: respLayoutDate } = useQuery({
    queryKey: ['lastLoadLayoutDateService'],
    queryFn: () => lastLoadLayoutDateService(),
    staleTime: QUERY_STALE_TIME_DEFAULT,
    enabled: withDates,
  });

  React.useEffect(() => {
    if (!withDates) return;
    if (respLayoutDate && !dates.startDate && !dates.endDate) {
      const { data } = respLayoutDate;
      if (!data) return;
      const date = data.data;
      const defaultStartDate = moment(
        getFirstDayOfTheMonth(new Date(date)),
      ).format('YYYY-MM-DD');

      dispatch({
        type: `${String(sliceName)}/setStartDate`,
        payload: defaultStartDate,
      });
      dispatch({
        type: `${String(sliceName)}/setEndDate`,
        payload: date || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [respLayoutDate, dates]);

  return {
    dispatch,
    // Selectores
    data: useSelector(selectors.getData),
    filters: useSelector(selectors.getFilters),
    dataQuery: useSelector(selectors.getDataQuery),
    openFilter: useSelector(selectors.getOpenFilter),
    cleanFilters: useSelector(selectors.getCleanFilters),
    loading: useSelector(selectors.getLoading),
    isSubmitting: useSelector(selectors.getSubmit),
    total: useSelector(selectors.getTotal),
    page: useSelector(selectors.getPage),
    pageSize: useSelector(selectors.getPageSize),
    showTooltip: useSelector(selectors.getShowTooltip),
    filterOpened: useSelector(selectors.getFilterOpened),
    filtersObject: useSelector(selectors.getFiltersObject),
    dates,
    handleOpenFilters: () =>
      dispatch({ type: `${String(sliceName)}/toggleFilterSidebar` }),
    // Métodos para actualizar el store
    setData: (payload: any) =>
      dispatch({ type: `${String(sliceName)}/setData`, payload }),
    setFilterValues: (payload: any) =>
      dispatch({ type: `${String(sliceName)}/setFilterValues`, payload }),
    setFilterValue: (payload: any) =>
      dispatch({ type: `${String(sliceName)}/setFilterValue`, payload }),
    setDataQuery: (payload: any) =>
      dispatch({ type: `${String(sliceName)}/setDataQuery`, payload }),
    setSubmit: (payload: boolean) =>
      dispatch({ type: `${String(sliceName)}/setSubmit`, payload }),
    setSendFilters: (payload: boolean) =>
      dispatch({ type: `${String(sliceName)}/setSendFilters`, payload }),
    resetPage: () => dispatch({ type: `${String(sliceName)}/resetPagination` }),
    cleanInputs: (payload: boolean) =>
      dispatch({ type: `${String(sliceName)}/cleanInputs`, payload }),
    cleanDataQuery: () => dispatch({ type: `${String(sliceName)}/cleanAll` }),
    cleanDate: () => dispatch({ type: `${String(sliceName)}/cleanDate` }),
    closeAllFilters: () =>
      dispatch({ type: `${String(sliceName)}/closeAllFilters` }),
    setOpenFilter: (payload: { name: string }) =>
      dispatch({ type: `${String(sliceName)}/setOpenFilter`, payload }),
    setOpenTooltip: (payload: boolean) =>
      dispatch({ type: `${String(sliceName)}/setOpenTooltip`, payload }),
    setPage: (payload: number) =>
      dispatch({ type: `${String(sliceName)}/setPage`, payload }),
    setPageSize: (payload: number) =>
      dispatch({ type: `${String(sliceName)}/setPageSize`, payload }),
    setComplete: (payload: boolean) =>
      dispatch({ type: `${String(sliceName)}/setComplete`, payload }),
    setLoading: (payload: boolean) =>
      dispatch({ type: `${String(sliceName)}/setLoading`, payload }),
    setStartDate: (payload: string) =>
      dispatch({ type: `${String(sliceName)}/setStartDate`, payload }),
    setEndDate: (payload: string) =>
      dispatch({ type: `${String(sliceName)}/setEndDate`, payload }),

    // Handlers comunes para paginación
    changePagination: (
      event: React.MouseEvent<HTMLButtonElement> | null,
      value: number,
    ) => {
      dispatch({ type: `${String(sliceName)}/setPage`, payload: value + 1 });
      dispatch({ type: `${String(sliceName)}/setSendFilters`, payload: true });
    },
    handleChangeRowsPerPage: (event: React.ChangeEvent<any>) => {
      dispatch({
        type: `${String(sliceName)}/setPageSize`,
        payload: event.target.value,
      });
      dispatch({ type: `${String(sliceName)}/setPage`, payload: 1 });
      dispatch({ type: `${String(sliceName)}/setSendFilters`, payload: true });
    },

    handleApplyFilters: (resetPagination = true) =>
      dispatch({
        type: `${String(sliceName)}/applyFilters`,
        payload: { resetPagination },
      }),

    // Handler común para cambio de fechas
    handleChangeDates: (newDates: [Date, Date]) => {
      const [startDateValue, endDateValue] = newDates;

      dispatch({
        type: `${String(sliceName)}/setStartDate`,
        payload: startDateValue
          ? moment(startDateValue).format('YYYY-MM-DD')
          : '',
      });
      dispatch({
        type: `${String(sliceName)}/setEndDate`,
        payload: endDateValue ? moment(endDateValue).format('YYYY-MM-DD') : '',
      });

      if (endDateValue) {
        dispatch({
          type: `${String(sliceName)}/applyFilters`,
          payload: { resetPagination: true },
        });
      }
    },
  };
};

export default useReportStore;
